import React from 'react'
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  useWindowDimensions,
  Platform
} from 'react-native'
import { MaterialCommunityIcons } from '@expo/vector-icons'
import { useTranslation } from 'react-i18next'
import { fontPalette } from '../../static/styles/global'
import { useshiftstore } from '../../stores/shift'
import { LeaveBalanceItem } from '../../types/shiftTypes'

const formatHours = (val: number | string | undefined | null): string => {
  if (val === undefined || val === null || val === '') return '0.00'
  const num = typeof val === 'string' ? parseFloat(val) : val
  return isNaN(num) ? '0.00' : num.toFixed(2)
}

const getLeaveCardConfig = (leaveType: string) => {
  const type = (leaveType || '').toUpperCase().trim()
  if (type === 'ANNUAL' || type === 'PTO') {
    return {
      title: 'PTO',
      iconName: 'palm-tree' as const,
      iconColor: '#0284C7',
      iconBgColor: '#E0F2FE',
      valueColor: '#0284C7'
    }
  }
  if (type === 'SICK' || type.includes('SICK')) {
    return {
      title: 'SICK TIME',
      iconName: 'medical-bag' as const,
      iconColor: '#7C3AED',
      iconBgColor: '#F3E8FF',
      valueColor: '#7C3AED'
    }
  }
  if (type === 'PERSONAL' || type.includes('PERSONAL')) {
    return {
      title: 'PERSONAL TIME',
      iconName: 'calendar-month-outline' as const,
      iconColor: '#16A34A',
      iconBgColor: '#DCFCE7',
      valueColor: '#16A34A'
    }
  }
  return {
    title: leaveType || '',
    iconName: 'calendar-month-outline' as const,
    iconColor: '#0284C7',
    iconBgColor: '#E0F2FE',
    valueColor: '#0284C7'
  }
}

const LeaveBalancesCards: React.FC = () => {
  const { t } = useTranslation()
  const { width } = useWindowDimensions()
  const shiftStore = useshiftstore()
  const isWide = width >= 860

  const list: LeaveBalanceItem[] = shiftStore.leaveBalancesData?.data || []

  return (
    <View style={styles.container}>
      {/* Header section with calendar icon */}
      <View style={styles.headerRow}>
        <Text style={styles.headerTitle}>{t('Leave Balances')}</Text>
      </View>

      {/* Cards list */}
      {shiftStore.loading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="small" color="#FFFFFF" />
        </View>
      ) : (
        <View
          style={[
            styles.cardsWrapper,
            isWide ? styles.cardsRow : styles.cardsColumn
          ]}
        >
          {list.map((item, index) => {
            const cardConfig = getLeaveCardConfig(item.leaveType)
            const availableHours = formatHours(
              item.avaliableBalance ?? item.availableBalance
            )
            const accruedHours = formatHours(item.accruedYTD)
            const usedHours = formatHours(item.usedYTD)

            return (
              <View
                key={`${item.leaveType}-${index}`}
                style={[
                  styles.card,
                  isWide ? styles.cardFlex : styles.cardFullWidth
                ]}
              >
                {/* Left Side: Icon & Available Balance */}
                <View style={styles.leftSection}>
                  <View
                    style={[
                      styles.iconCircle,
                      { backgroundColor: cardConfig.iconBgColor }
                    ]}
                  >
                    <MaterialCommunityIcons
                      name={cardConfig.iconName}
                      size={22}
                      color={cardConfig.iconColor}
                    />
                  </View>

                  <View style={styles.leftInfo}>
                    <Text style={styles.cardTitle}>{t(cardConfig.title)}</Text>
                    <View style={styles.balanceRow}>
                      <Text
                        style={[
                          styles.availableValue,
                          { color: cardConfig.valueColor }
                        ]}
                      >
                        {availableHours}
                      </Text>
                      <Text style={styles.hoursUnit}>{t('hours')}</Text>
                    </View>
                    <Text style={styles.balanceSubtext}>
                      {t('Available Balance')}
                    </Text>
                  </View>
                </View>

                {/* Vertical Divider */}
                <View style={styles.divider} />

                {/* Right Side: Accrued YTD & Used YTD */}
                <View style={styles.rightSection}>
                  <View style={styles.ytdBlock}>
                    <Text style={styles.ytdValue}>
                      {accruedHours} {t('hours')}
                    </Text>
                    <Text style={styles.ytdLabel}>{t('Accrued YTD')}</Text>
                  </View>

                  <View style={[styles.ytdBlock, { marginTop: 8 }]}>
                    <Text style={styles.ytdValue}>
                      {usedHours} {t('hours')}
                    </Text>
                    <Text style={styles.ytdLabel}>{t('Used YTD')}</Text>
                  </View>
                </View>
              </View>
            )
          })}
        </View>
      )}
    </View>
  )
}

export default LeaveBalancesCards

const styles = StyleSheet.create({
  container: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    width: '100%'
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 14
  },
  headerIconBadge: {
    backgroundColor: '#0284C7',
    borderRadius: 6,
    padding: 4,
    marginRight: 8,
    justifyContent: 'center',
    alignItems: 'center'
  },
  headerTitle: {
    fontSize: 16,
    fontFamily: fontPalette.primaryBold,
    fontWeight: '700',
    color: '#FFFFFF',
    letterSpacing: 0.3
  },
  loadingContainer: {
    paddingVertical: 24,
    justifyContent: 'center',
    alignItems: 'center'
  },
  cardsWrapper: {
    width: '100%'
  },
  cardsRow: {
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: 12
  },
  cardsColumn: {
    flexDirection: 'column',
    gap: 12
  },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    paddingVertical: 14,
    paddingHorizontal: 14,
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    ...Platform.select({
      web: {
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)'
      },
      default: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.06,
        shadowRadius: 6,
        elevation: 2
      }
    })
  },
  cardFlex: {
    flex: 1,
    minWidth: 240
  },
  cardFullWidth: {
    width: '100%'
  },
  leftSection: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    paddingRight: 8
  },
  iconCircle: {
    width: 44,
    height: 44,
    borderRadius: 22,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 10
  },
  leftInfo: {
    flex: 1,
    justifyContent: 'center'
  },
  cardTitle: {
    fontSize: 12,
    fontFamily: fontPalette.primaryBold,
    fontWeight: '700',
    color: '#0B495C',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    marginBottom: 2
  },
  balanceRow: {
    flexDirection: 'row',
    alignItems: 'baseline'
  },
  availableValue: {
    fontSize: 18,
    fontFamily: fontPalette.primaryBold,
    fontWeight: '800',
    lineHeight: 22
  },
  hoursUnit: {
    fontSize: 11,
    fontFamily: fontPalette.primaryRegular,
    color: '#64748B',
    marginLeft: 3
  },
  balanceSubtext: {
    fontSize: 11,
    fontFamily: fontPalette.primaryRegular,
    color: '#64748B',
    marginTop: 2
  },
  divider: {
    width: 1,
    height: 46,
    backgroundColor: '#E2E8F0',
    marginHorizontal: 8
  },
  rightSection: {
    justifyContent: 'center',
    minWidth: 80,
    paddingLeft: 4
  },
  ytdBlock: {
    justifyContent: 'center'
  },
  ytdValue: {
    fontSize: 12,
    fontFamily: fontPalette.primaryBold,
    fontWeight: '700',
    color: '#1E293B'
  },
  ytdLabel: {
    fontSize: 10,
    fontFamily: fontPalette.primaryRegular,
    color: '#64748B',
    marginTop: 1
  }
})
